import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../../firebase';
import {
  ArrowLeft, CheckCircle2, Lock, BookOpen, FileText, Award,
  GraduationCap, Trophy, Target, ChevronRight, LockOpen, Layers,
  FileQuestion, LogOut, Home, ChevronsLeft, ChevronsRight, Play
} from 'lucide-react';
import '../../styles/CursosVortex.css';
import { useCourseProgress } from '../../hooks/useCourseProgress';
import { normalizeCourse, getTemas, countCompletedTemas, isTemaCompletado, isCourseCompletado, getTemaExamenFinal, isTemaExamenesAprobados, getLessonExamenes, getLessonFlashcards, PASS_THRESHOLD } from '../../utils/courseStructure';
import { getAcademyQuiz } from '../../services/academyQuizzes';
import { LessonContent } from './LessonContent';
import { ExamFinal } from './ExamFinal';
import { CertificateCard } from './CertificateCard';
import { LectorTemas } from './LectorTemas';
import { FlashcardsDeck } from './FlashcardsDeck';
import { ExamRunner, buildFlashcards, LECTOR_AREAS } from './LectorTemasView';
import { ProtectedAudio, ProtectedImage, ProtectedVideo, InlinePdf, ProtectedLink } from './ProtectedMedia';
import {
  getAreaTemas, getTemas as getLectorTemas,
  watchAreaCuestionarios, watchCuestionarios,
  AREA_NAMES, areaKeyForId,
  getPreguntasPorTema, sampleFinalQuestions,
} from '../../services/academyFirestore';

const S2 = {
  tag: {
    display: 'inline-block', fontSize: '0.62rem', fontWeight: 800, letterSpacing: '1px', textTransform: 'uppercase',
    color: 'var(--gold-light)', marginBottom: '6px',
  },
  link: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', textAlign: 'center',
    padding: '16px', borderRadius: '12px', textDecoration: 'none',
    border: '1px solid rgba(197,160,89,0.3)', background: 'rgba(197,160,89,0.08)',
    color: '#e2e8f0', transition: 'background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease, transform 0.15s ease',
  },
};

// Convierte un nombre de módulo/lección al mismo slug que usa el generador
// para guardar los sets de multimedia (modulo-2, leccion-3, etc.).
const multimediaSlug = (s) => (s || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'unidad';

export const VirtualClassroom = ({ course, uid, userName = 'Estudiante', userEmail = '', academyProfile, onBackToCatalog, isSubscribed = true }) => {
  const navigate = useNavigate();
  const normCourse = useMemo(() => normalizeCourse(course), [course]);
  const temas = getTemas(normCourse);

  const { progress, updateProgress } = useCourseProgress(normCourse?.id, uid);

  // Selección activa: lección, examen de tema, examen final del tema, o lector
  const [active, setActive] = useState(null); // { type:'lesson', lesson } | { type:'exam', tema, exam } | { type:'temaFinal', tema } | { type:'lector' }
  const [activeExamResult, setActiveExamResult] = useState(null);
  // Panel de módulos de estudio: contraído / expandido hacia el costado
  const [navCollapsed, setNavCollapsed] = useState(false);
  // Preguntas del examen activo cuando provienen de la ruta Vortex Academy (exam.quizId)
  const [examAcademyQuestions, setExamAcademyQuestions] = useState(null);
  const [loadingExamAcademy, setLoadingExamAcademy] = useState(false);
  // Actividades de estudio expandidas por clave (lector_<id> / lesson_<temaId>_<lessonId>).
  // Vacío por defecto: todas las actividades aparecen contraídas.
  const [expandedActivities, setExpandedActivities] = useState(() => new Set());

  // ── Lector de Temas integrado ──
  const [lectorAreas, setLectorAreas] = useState([]); // [{ areaKey, label, temas }]
  const [lectorLoading, setLectorLoading] = useState(false);
  const [selLector, setSelLector] = useState(null); // { areaKey, tema }
  const [lectorTab, setLectorTab] = useState('lectura'); // 'lectura' | 'flashcards' | 'examen'
  const [lectorCuestionarios, setLectorCuestionarios] = useState([]);
  const [activeQuiz, setActiveQuiz] = useState(null); // cuestionario a ejecutar
  // Examen final del tema del Lector: 20 preguntas aleatorias del banco RTDB.
  const [lectorFinalQuestions, setLectorFinalQuestions] = useState([]);
  const [lectorFinalLoading, setLectorFinalLoading] = useState(false);
  const [lectorFinalNonce, setLectorFinalNonce] = useState(0);
  const [lectorFinalRetry, setLectorFinalRetry] = useState(false);
  const [lectorActiveLesson, setLectorActiveLesson] = useState(null);

  const { jerarquia, cuerpoServicio: cuerpo, especialidad } = academyProfile || {};

  // Área del curso actual (por su categoría o area_id). El Lector integrado solo
  // debe mostrar los módulos de ESA área, no todas las áreas del sistema.
  const courseAreaKey = useMemo(() => {
    if (normCourse?.area_id) return areaKeyForId(normCourse.area_id);
    const norm = (s) => String(s || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();
    const cat = norm(normCourse?.category || normCourse?.title);
    const match = Object.entries(AREA_NAMES).find(([, name]) => {
      const n = norm(name);
      return cat === n || cat.includes(n) || n.includes(cat);
    });
    return match ? match[0] : null;
  }, [normCourse?.area_id, normCourse?.category, normCourse?.title]);

  // Carga los temas del Lector del área del curso, filtrados por la jerarquía del usuario.
  useEffect(() => {
    let cancelled = false;
    if (!jerarquia) { setLectorAreas([]); setLectorLoading(false); return undefined; }
    if (!courseAreaKey) { setLectorAreas([]); setLectorLoading(false); return undefined; }
    setLectorLoading(true);
    Promise.all(
      LECTOR_AREAS
        .filter((a) => a.key === courseAreaKey)
        .map(async (a) => {
          const list = a.key
            ? await getAreaTemas(a.key)
            : await getLectorTemas({ jerarquia, cuerpo, especialidad });
          const visibles = a.key
            ? list.filter((t) => t.grados.includes('TODOS') || t.grados.includes(jerarquia))
            : list;
          return { areaKey: a.key, label: a.label, temas: visibles };
        }),
    )
      .then((groups) => { if (!cancelled) setLectorAreas(groups); })
      .catch((e) => console.error('[VirtualClassroom] Lector:', e))
      .finally(() => { if (!cancelled) setLectorLoading(false); });
    return () => { cancelled = true; };
  }, [jerarquia, cuerpo, especialidad, courseAreaKey]);

  // Cuestionarios / flashcards del tema del Lector activo
  useEffect(() => {
    if (!selLector) { setLectorCuestionarios([]); return undefined; }
    const { areaKey, tema } = selLector;
    return areaKey
      ? watchAreaCuestionarios(areaKey, tema.id, (list) => setLectorCuestionarios(list))
      : watchCuestionarios({ jerarquia, cuerpo, especialidad, temaId: tema.id }, (list) => setLectorCuestionarios(list));
  }, [selLector, jerarquia, cuerpo, especialidad]);

  // Examen final del tema del Lector: cada vez que se abre la pestaña "final" (o se
  // reintenta) se muestrean 20 preguntas aleatorias del banco RTDB (siempre diferentes).
  const loadLectorFinalQuestions = (asRetry = false) => {
    if (!selLector || !lectorTemaHasFinal(selLector.tema)) return;
    const { tema } = selLector;
    setLectorFinalLoading(true);
    setLectorFinalQuestions([]);
    setLectorFinalRetry(!!asRetry);
    getPreguntasPorTema(tema.id)
      .then((qs) => {
        const banco = sampleFinalQuestions(qs, 20);
        if (banco.length > 0) {
          setLectorFinalQuestions(banco);
        } else {
          // Fallback: si el banco RTDB no tiene preguntas, se arma el examen
          // final con las autoevaluaciones de las lecciones del tema.
          const lessonQs = (tema.lessons || [])
            .flatMap((l) =>
              (l.quizQuestions || [])
                .filter((q) => q && q.question && (q.options || []).length > 1)
                .map((q, idx) => ({
                  id: `${l.id}_${idx}`,
                  question: q.question,
                  options: (q.options || []).map(String),
                  correct_index: Number.isInteger(q.correct_index) ? q.correct_index : 0,
                  explanation: q.explanation || '',
                }))
            );
          setLectorFinalQuestions(sampleFinalQuestions(lessonQs, 20));
        }
        setLectorFinalNonce((n) => n + 1);
      })
      .catch((e) => console.error('[VirtualClassroom] Error cargando banco RTDB del tema:', e))
      .finally(() => setLectorFinalLoading(false));
  };

  useEffect(() => {
    if (!selLector || lectorTab !== 'final') return;
    loadLectorFinalQuestions();
  }, [selLector, lectorTab]);

  const lectorFlashcards = buildFlashcards(lectorCuestionarios);

  const openLectorTema = (areaKey, tema, tab = 'lectura') => {
    setSelLector({ areaKey, tema });
    setLectorTab(tab);
    setLectorActiveLesson(tema.lessons?.[0] || null);
    setActive({ type: 'lector' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    const firstTema = temas[0];
    if (firstTema) {
      const firstLesson = firstTema.lessons?.[0];
      setActive(firstLesson ? { type: 'lesson', lesson: firstLesson } : { type: 'tema', tema: firstTema });
    } else {
      setActive(null);
    }
  }, [normCourse?.id]);

  const temasProg = progress?.temas || {};
  const quizScores = progress?.quizScores || {};

  // Temas del Lector del área del curso, unificados con los del RTDB para el progreso
  const lectorTemas = useMemo(
    () => lectorAreas.flatMap((g) => (g.temas || []).map((t) => ({ ...t, isLector: true, areaKey: g.areaKey }))),
    [lectorAreas]
  );
  const isLectorTemaCompletado = (tema) => !!temasProg[tema.id]?.completed;
  // El tema del Lector tiene examen final cuando proviene del banco RTDB (áreas generales
  // 1-25): `cuestionariosCount > 0` indica que el banco de preguntas fue importado.
  const lectorTemaHasFinal = (tema) =>
    (tema?.cuestionariosCount || 0) > 0 ||
    (tema?.lessons || []).some((l) => (l.quizQuestions || []).length > 0);
  const lectorFinalExamId = (tema) => `final_${tema.id}`;
  const lectorTemasDone = lectorTemas.every(isLectorTemaCompletado);
  const rtdbTemasDone = temas.length === 0 ? true : isCourseCompletado(normCourse, progress);

  const totalTemas = temas.length + lectorTemas.length;
  const completedTemas = countCompletedTemas(normCourse, progress) + lectorTemas.filter(isLectorTemaCompletado).length;
  const progressPct = totalTemas > 0 ? Math.round((completedTemas / totalTemas) * 100) : 0;
  const finalLiberado = rtdbTemasDone && lectorTemasDone;

  // Certificados POR TEMA/MÓDULO: cada tema (RTDB o del Lector) con su examen
  // final aprobado desbloquea su propio certificado descargable.
  const temaCertificates = useMemo(() => {
    const list = [];
    temas.forEach((t) => {
      const fe = getTemaExamenFinal(t);
      const exam = fe ? temasProg[t.id]?.examenes?.[fe.id] : undefined;
      list.push({
        key: `rtdb_${t.id}`,
        title: t.title,
        enabled: isTemaCompletado(t, progress),
        score: exam?.score,
        date: exam?.date,
      });
    });
    lectorTemas.forEach((t) => {
      const finalId = lectorFinalExamId(t);
      const exam = temasProg[t.id]?.examenes?.[finalId];
      list.push({
        key: `lector_${t.id}`,
        title: t.title,
        enabled: isLectorTemaCompletado(t),
        score: exam?.score,
        date: exam?.date,
      });
    });
    return list;
  }, [temas, lectorTemas, progress, temasProg]);

  const badges = [
    { id: 'first', label: 'Primera Lección', earned: completedTemas > 0, icon: GraduationCap },
    { id: 'quiz', label: 'Quiz Perfecto', earned: Object.values(quizScores).some((s) => s >= 100), icon: Target },
    { id: 'exam', label: 'Examen Aprobado', earned: finalLiberado, icon: Award },
    { id: 'complete', label: 'Curso Completado', earned: finalLiberado, icon: Trophy },
  ];

  // Defensa en profundidad: sin licencia activa en licencesPremium no se
  // renderiza ningún contenido del aula.
  if (!isSubscribed) {
    return (
      <div style={{ minHeight: '100vh', background: '#0a1a2a', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
        <div style={{ maxWidth: '440px', textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div style={{ width: '72px', height: '72px', borderRadius: '50%', margin: '0 auto', background: 'rgba(197,160,89,0.12)', border: '1px solid rgba(197,160,89,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Lock size={32} style={{ color: 'var(--gold-matte)' }} />
          </div>
          <h2 style={{ fontFamily: 'var(--font-tactical)', color: '#fff', margin: 0 }}>Contenido bloqueado</h2>
          <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', lineHeight: 1.6, margin: 0 }}>
            Necesitas una licencia activa en <strong style={{ color: '#fff' }}>licencesPremium</strong> para acceder a este curso.
            Adquiere tu licencia para desbloquear el aula, el lector de temas y todo el material.
          </p>
          <button onClick={onBackToCatalog} className="btn-vx-action" style={{ alignSelf: 'center', padding: '10px 20px' }}>
            Volver al catálogo
          </button>
        </div>
      </div>
    );
  }

  const saveTemaExamResult = (temaId, examId, result) => {
    const temaProg = temasProg[temaId] || {};
    updateProgress({
      temas: {
        ...temasProg,
        [temaId]: { ...temaProg, examenes: { ...(temaProg.examenes || {}), [examId]: { ...result, date: Date.now() } } },
      },
    });
  };

  // El Lector marca el tema completado solo por los quizzes (sin quizzes,
  // terminando la lectura) SI el tema no tiene examen final RTDB. Si lo tiene,
  // los quizzes solo liberan el examen final.
  const handleLectorReadFinish = (tema) => {
    if (lectorCuestionarios.length > 0) return;
    if (lectorTemaHasFinal(tema)) return;
    const temaId = tema.id;
    const temaProg = temasProg[temaId] || {};
    updateProgress({
      temas: { ...temasProg, [temaId]: { ...temaProg, completed: true } },
    });
  };

  // Guarda el avance de una lección del Lector completada (persistido por lector).
  const handleLessonComplete = (temaId, lessonId) => {
    const temaProg = temasProg[temaId] || {};
    updateProgress({
      temas: {
        ...temasProg,
        [temaId]: {
          ...temaProg,
          lessons: { ...(temaProg.lessons || {}), [lessonId]: true },
        },
      },
    });
  };

  const handleLectorQuizResult = (tema, quiz, result) => {
    const temaId = tema.id;
    const temaProg = temasProg[temaId] || {};
    const examenes = { ...(temaProg.examenes || {}), [quiz.id]: { ...result, date: Date.now() } };
    const allPassed = lectorCuestionarios.length > 0 && lectorCuestionarios.every((q) => {
      const r = examenes[q.id];
      return r && r.score >= PASS_THRESHOLD;
    });
    // Si el tema tiene examen final RTDB, el completado se marca al aprobarlo
    // (handleLectorFinalResult); los quizzes solo lo liberan.
    const completed = allPassed && !lectorTemaHasFinal(tema);
    updateProgress({
      quizScores: { ...quizScores, [`lector_${temaId}_${quiz.id}`]: result.score },
      temas: {
        ...temasProg,
        [temaId]: {
          ...temaProg,
          examenes,
          ...(completed ? { completed: true } : {}),
        },
      },
    });
  };

  // Examen final del tema del Lector: 20 preguntas aleatorias del banco RTDB.
  // Aprobarlo marca el tema como completado.
  const handleLectorFinalResult = (tema, result) => {
    const temaId = tema.id;
    const finalExamId = lectorFinalExamId(tema);
    const temaProg = temasProg[temaId] || {};
    const examenes = { ...(temaProg.examenes || {}), [finalExamId]: { ...result, date: Date.now() } };
    updateProgress({
      quizScores: { ...quizScores, [`lector_${temaId}_${finalExamId}`]: result.score },
      temas: {
        ...temasProg,
        [temaId]: { ...temaProg, examenes, ...(result.passed ? { completed: true } : {}) },
      },
    });
  };

  const openLesson = (lesson) => {
    setActiveExamResult(null);
    setActive({ type: 'lesson', lesson });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const openFlashcards = (tema, lesson) => {
    setActiveExamResult(null);
    setActive({ type: 'flashcards', tema, lesson });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const openMaterial = (tema, lesson) => {
    setActiveExamResult(null);
    setActive({ type: 'material', tema, lesson });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const openExam = (tema, lesson, exam) => {
    const stored = temasProg[tema.id]?.examenes?.[exam.id];
    setActiveExamResult(stored || null);
    setExamAcademyQuestions(null);
    setActive({ type: 'exam', tema, lesson, exam });
    if (exam.quizId && academyProfile?.jerarquia) {
      setLoadingExamAcademy(true);
      getAcademyQuiz({
        jerarquia: academyProfile.jerarquia,
        cuerpo: academyProfile.cuerpoServicio,
        especialidad: academyProfile.especialidad,
        quizId: exam.quizId,
      })
        .then((quiz) => setExamAcademyQuestions(quiz?.questions || null))
        .catch((e) => console.error('[VirtualClassroom] Error cargando examen academy:', e))
        .finally(() => setLoadingExamAcademy(false));
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const openTemaFinal = (tema) => {
    const finalExam = getTemaExamenFinal(tema);
    if (!finalExam) return;
    const stored = temasProg[tema.id]?.examenes?.[finalExam.id];
    setActiveExamResult(stored || null);
    setExamAcademyQuestions(null);
    setActive({ type: 'temaFinal', tema });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleExitToHome = () => {
    navigate('/');
  };

  const handleLogout = async () => {
    try { await signOut(auth); } catch (e) { console.error('[VirtualClassroom] Logout:', e); }
    navigate('/');
  };

  // Pliega/expande el bloque "Actividades de estudio" de un tema o lección.
  const toggleActivities = (key) => {
    setExpandedActivities((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // ── Render principal (lección / examen / final) ──
  const renderMainContent = () => {
    // Lector de Temas: la actividad activa (lectura / flashcards / quiz evaluativo) se
    // selecciona desde el sidebar (ACTIVIDADES DE ESTUDIO) y se muestra aquí.
    if (active?.type === 'lector' && selLector) {
      const { areaKey, tema } = selLector;
      const area = LECTOR_AREAS.find((a) => a.key === areaKey);
      const tabBtn = (id, label, count) => {
        const Icon = id === 'lectura' ? BookOpen : id === 'flashcards' ? Layers : id === 'final' ? Award : FileQuestion;
        const isTab = lectorTab === id;
        return (
          <button
            key={id}
            type="button"
            onClick={() => setLectorTab(id)}
            style={{
              padding: '8px 14px', borderRadius: '10px', cursor: 'pointer', fontSize: '0.74rem', fontWeight: 700,
              fontFamily: 'var(--font-primary)', display: 'flex', alignItems: 'center', gap: '6px',
              border: isTab ? '1px solid rgba(197,160,89,0.5)' : '1px solid rgba(255,255,255,0.1)',
              background: isTab ? 'rgba(197,160,89,0.15)' : 'rgba(255,255,255,0.03)',
              color: isTab ? 'var(--gold-light)' : '#94a3b8',
            }}
          >
            <Icon size={14} />
            {label}
            {typeof count === 'number' && <span style={{ fontSize: '0.66rem', opacity: 0.8 }}>({count})</span>}
          </button>
        );
      };

      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          <div>
            <span style={{ fontSize: '0.72rem', color: 'var(--gold-matte)', fontFamily: 'monospace', textTransform: 'uppercase', fontWeight: 'bold' }}>
              📖 Lector de Temas · {area?.label || 'Área'}
            </span>
            <h1 style={{ fontFamily: 'var(--font-tactical)', fontSize: '1.4rem', color: '#fff', margin: '4px 0 0' }}>
              {tema.title}
            </h1>
            <p style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', margin: '6px 0 0' }}>
              {tema.lessons.length} lección(es) · {lectorCuestionarios.length} cuestionario(s) · {jerarquia}{cuerpo ? ` · ${cuerpo}` : ''}
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '12px' }}>
              {tabBtn('lectura', '📖 Lectura')}
              {tabBtn('flashcards', '🗂️ Flashcards', lectorFlashcards.length)}
              {tabBtn('examen', '📝 Quiz evaluativo', lectorCuestionarios.length)}
              {lectorTemaHasFinal(tema) && tabBtn('final', '🏆 Examen Final', 20)}
            </div>
          </div>

          {/* ── Lectura: lector + material audiovisual ── */}
          {lectorTab === 'lectura' && (
            <>
              <div style={{ background: '#0f2035', border: '1px solid rgba(197,160,89,0.25)', borderRadius: '16px', padding: '1.25rem' }}>
                <h2 style={{ fontFamily: 'var(--font-tactical)', fontSize: '1rem', color: '#fff', margin: '0 0 10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <BookOpen size={16} style={{ color: 'var(--gold-matte)' }} /> Lectura · Puntos clave
                </h2>
                <LectorTemas
                  courseTitle={tema.title}
                  moduleLabel={tema.module || area?.label}
                  lessons={tema.lessons}
                  initialCompleted={temasProg[tema.id]?.lessons || {}}
                  onLessonComplete={(lessonId) => handleLessonComplete(tema.id, lessonId)}
                  onFinish={() => handleLectorReadFinish(tema)}
                  hasFinalExam={lectorTemaHasFinal(tema)}
                  onFinalExam={() => { setLectorTab('final'); loadLectorFinalQuestions(); }}
                  onActiveLessonChange={setLectorActiveLesson}
                />
              </div>

              {tema.multimedia && (() => {
                const mm = tema.multimedia;
                const cur = lectorActiveLesson;
                // Si la lección activa ya trae sus propios enlaces (guardado
                // automático del generador), no duplicar nada aquí abajo.
                const lessonHasOwn = !!(
                  cur && (cur.video_url || cur.audio_url || cur.infographic_url || cur.slides_url)
                );
                if (lessonHasOwn) return null;
                const hasLegacy = !!(mm.audio_url || mm.infographic_url || mm.slides_url || mm.report_url || mm.video_url);
                const raw = (mm.items && typeof mm.items === 'object') ? mm.items : (hasLegacy ? { tema: mm } : {});
                // Mostrar solo el set de la unidad activa (o el global "tema").
                const candidates = cur
                  ? [multimediaSlug(cur.module), multimediaSlug(cur.id), multimediaSlug(cur.title)]
                  : [];
                const items = Object.entries(raw)
                  .filter(([key]) => key === 'tema' || candidates.includes(key))
                  .map(([key, it]) => ({ key, ...(typeof it === 'object' ? it : {}) }));
                if (!items.length) return null;
                return (
                  <div style={{ background: '#0f2035', border: '1px solid rgba(197,160,89,0.35)', borderRadius: '16px', padding: '1.25rem' }}>
                    <h2 style={{ fontFamily: 'var(--font-tactical)', fontSize: '1rem', color: '#fff', margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      🎙️ Material audiovisual <span style={{ fontSize: '0.66rem', color: 'var(--text-muted)', fontWeight: 400 }}>
                        {mm.generatedAt ? `· ${new Date(mm.generatedAt).toLocaleDateString('es-MX')}` : ''}
                        {items.length > 1 ? ` · ${items.length} sets` : ''}
                      </span>
                    </h2>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      {items.map((it) => (
                        <div key={it.key} style={items.length > 1 ? { borderTop: '1px solid rgba(197,160,89,0.15)', paddingTop: '12px' } : undefined}>
                          {(it.title || items.length > 1) && (
                            <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#c5a059', margin: '0 0 8px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                              {it.title || (mm.scope === 'lesson' ? 'Lección' : 'Módulo')}
                            </div>
                          )}
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {it.video_url && (
                              <div>
                                <span style={S2.tag}>🎬 Video explicativo</span>
                                <ProtectedVideo src={it.video_url} style={{ width: '100%', marginTop: '8px', borderRadius: '10px', background: '#000' }} />
                              </div>
                            )}
                            {it.audio_url && (
                              <div>
                                <span style={S2.tag}>● Podcast / resumen de audio</span>
                                <ProtectedAudio src={it.audio_url} style={{ width: '100%', marginTop: '8px' }} />
                              </div>
                            )}
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px' }}>
                              {it.infographic_url && (
                                <ProtectedImage src={it.infographic_url} alt="Infografía" caption="Infografía · solo lectura" />
                              )}
                              {it.report_url && (
                                <ProtectedLink href={it.report_url} style={S2.link}>
                                  <span style={{ fontSize: '1.2rem' }}>📄</span>
                                  <span style={{ fontSize: '0.75rem', fontWeight: 700 }}>Análisis ↗</span>
                                </ProtectedLink>
                              )}
                            </div>
                            {it.slides_url && (
                              <InlinePdf url={it.slides_url} name="Presentación de diapositivas (solo lectura)" />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </>
          )}

          {/* ── Flashcards del tema ── */}
          {lectorTab === 'flashcards' && (
            <div style={{ background: '#0f2035', border: '1px solid rgba(197,160,89,0.25)', borderRadius: '16px', padding: '1.25rem' }}>
              <h2 style={{ fontFamily: 'var(--font-tactical)', fontSize: '1rem', color: '#fff', margin: '0 0 10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                🗂️ Flashcards <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>({lectorFlashcards.length})</span>
              </h2>
              {lectorFlashcards.length > 0 ? (
                <FlashcardsDeck flashcards={lectorFlashcards} />
              ) : (
                <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                  No hay flashcards disponibles para este tema todavía.
                </p>
              )}
            </div>
          )}

          {/* ── Quiz Evaluativo del tema ── */}
          {lectorTab === 'examen' && (
            <div style={{ background: '#0f2035', border: '1px solid rgba(197,160,89,0.25)', borderRadius: '16px', padding: '1.25rem' }}>
              <h2 style={{ fontFamily: 'var(--font-tactical)', fontSize: '1rem', color: '#fff', margin: '0 0 10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                📝 Quiz Evaluativo ({lectorCuestionarios.length})
              </h2>
              {lectorCuestionarios.length === 0 ? (
                <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                  Este tema aún no tiene exámenes disponibles.
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {lectorCuestionarios.map((q) => {
                    const qResult = temasProg[tema.id]?.examenes?.[q.id];
                    const qPassed = !!(qResult && qResult.score >= PASS_THRESHOLD);
                    return (
                      <button
                        key={q.id}
                        type="button"
                        onClick={() => setActiveQuiz(q)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '14px', textAlign: 'left', cursor: 'pointer',
                          padding: '16px 18px', borderRadius: '14px', background: 'rgba(255,255,255,0.025)',
                          border: qPassed ? '1px solid rgba(52,211,153,0.4)' : '1px solid rgba(255,255,255,0.08)',
                        }}
                      >
                        <div style={{
                          width: '40px', height: '40px', borderRadius: '10px', flexShrink: 0,
                          background: qPassed ? 'rgba(16,185,129,0.15)' : 'rgba(245,158,11,0.12)',
                          border: qPassed ? '1px solid rgba(52,211,153,0.5)' : '1px solid rgba(245,158,11,0.3)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          {qPassed ? <CheckCircle2 size={18} style={{ color: '#34d399' }} /> : <FileQuestion size={18} style={{ color: '#f59e0b' }} />}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ margin: 0, fontSize: '0.9rem', fontWeight: 700, color: '#fff' }}>{q.title}</p>
                          <span style={{ fontSize: '0.7rem', color: '#94a3b8', fontFamily: 'monospace' }}>
                            {q.questions?.length || 0} preguntas
                            {qResult ? ` · ${qResult.score}%` : ''}
                          </span>
                        </div>
                        {qPassed && <CheckCircle2 className="w-4 h-4" style={{ color: '#34d399', flexShrink: 0 }} />}
                        <ChevronRight size={18} style={{ color: '#64748b', flexShrink: 0 }} />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          {/* ── Examen Final del tema del Lector (banco RTDB, 20 preguntas aleatorias) ── */}
          {lectorTab === 'final' && lectorTemaHasFinal(tema) && (
            <div style={{ background: '#0f2035', border: '1px solid rgba(197,160,89,0.25)', borderRadius: '16px', padding: '1.25rem' }}>
              <h2 style={{ fontFamily: 'var(--font-tactical)', fontSize: '1rem', color: '#fff', margin: '0 0 10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                🏆 Examen Final del Tema
              </h2>
              {(() => {
                const finalExamId = lectorFinalExamId(tema);
                const storedFinal = temasProg[tema.id]?.examenes?.[finalExamId];
                const lessons = tema.lessons || [];
                const lessonsDone = lessons.length === 0 || lessons.every((l) => temasProg[tema.id]?.lessons?.[l.id]);
                const quizzesDone = lectorCuestionarios.length > 0 && lectorCuestionarios.every((q) => {
                  const r = temasProg[tema.id]?.examenes?.[q.id];
                  return r && r.score >= PASS_THRESHOLD;
                });
                const unlocked = lessonsDone && (quizzesDone || lectorCuestionarios.length === 0);
                if (!unlocked) {
                  return (
                    <div style={{ textAlign: 'center', padding: '2rem 1rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                      <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: 'rgba(197,160,89,0.12)', border: '1px solid rgba(197,160,89,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Lock className="w-7 h-7" style={{ color: 'var(--gold-matte)' }} />
                      </div>
                      <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', margin: 0, maxWidth: '400px', lineHeight: 1.6 }}>
                        Completa todas las lecciones y aprueba sus autoevaluaciones para liberar el examen final del tema.
                      </p>
                    </div>
                  );
                }
                if (lectorFinalLoading) {
                  return <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>Generando preguntas del examen final...</p>;
                }
                if (lectorFinalQuestions.length === 0) {
                  return <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>El banco de preguntas de este tema aún no tiene preguntas para su examen final.</p>;
                }
                return (
                  <ExamFinal
                    key={lectorFinalNonce}
                    questions={lectorFinalQuestions}
                    initialResult={lectorFinalRetry ? null : storedFinal}
                    onFinish={(result) => handleLectorFinalResult(tema, result)}
                    onRetry={() => loadLectorFinalQuestions(true)}
                  />
                );
              })()}
            </div>
          )}
        </div>
      );
    }

    if (active?.type === 'lesson' && active.lesson) {
      return (
        <LessonContent lesson={active.lesson} />
      );
    }

    if (active?.type === 'flashcards' && active.lesson) {
      const flashcards = getLessonFlashcards(active.lesson);
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          <div>
            <span style={{ fontSize: '0.72rem', color: 'var(--gold-matte)', fontFamily: 'monospace', textTransform: 'uppercase', fontWeight: 'bold' }}>
              {active.tema.title} · Repaso
            </span>
            <h1 style={{ fontFamily: 'var(--font-tactical)', fontSize: '1.4rem', color: '#fff', margin: '4px 0 0' }}>
              🗂️ Flashcards · {active.lesson.title}
            </h1>
            <p style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', margin: '6px 0 0' }}>
              {flashcards.length} tarjeta(s) de repaso de esta lección. Voltea la tarjeta y marca las que ya dominas.
            </p>
          </div>
          <div style={{ background: '#0f2035', border: '1px solid rgba(197,160,89,0.25)', borderRadius: '16px', padding: '1.25rem' }}>
            <FlashcardsDeck flashcards={flashcards} />
          </div>
        </div>
      );
    }

    if (active?.type === 'material' && active.lesson) {
      const lesson = active.lesson;
      const images = (Array.isArray(lesson.images) ? lesson.images : []).filter((im) => im && im.url);
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          <div>
            <span style={{ fontSize: '0.72rem', color: 'var(--gold-matte)', fontFamily: 'monospace', textTransform: 'uppercase', fontWeight: 'bold' }}>
              {active.tema.title} · Apoyo Audiovisual
            </span>
            <h1 style={{ fontFamily: 'var(--font-tactical)', fontSize: '1.4rem', color: '#fff', margin: '4px 0 0' }}>
              🎬 Material de apoyo · {lesson.title}
            </h1>
            <p style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', margin: '6px 0 0' }}>
              Video explicativo, podcast y material visual para afianzar tu aprendizaje.
            </p>
          </div>

          {/* 1) Video */}
          <div style={{ background: '#0f2035', border: '1px solid rgba(197,160,89,0.25)', borderRadius: '16px', padding: '1.25rem' }}>
            <span style={S2.tag}>🎬 Video explicativo</span>
            {lesson.video_url ? (
              <ProtectedVideo src={lesson.video_url} style={{ width: '100%', marginTop: '8px', borderRadius: '10px', background: '#000' }} />
            ) : (
              <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontStyle: 'italic', marginTop: '8px' }}>
                Esta lección no tiene video de apoyo.
              </p>
            )}
          </div>

          {/* 2) Podcast */}
          <div style={{ background: '#0f2035', border: '1px solid rgba(197,160,89,0.25)', borderRadius: '16px', padding: '1.25rem' }}>
            <span style={S2.tag}>● Podcast / audio de apoyo</span>
            {lesson.audio_url ? (
              <>
                {lesson.audio_caption && (
                  <p style={{ fontSize: '0.78rem', fontWeight: 700, color: '#fff', margin: '8px 0 0' }}>{lesson.audio_caption}</p>
                )}
                <ProtectedAudio src={lesson.audio_url} style={{ width: '100%', marginTop: '8px' }} />
              </>
            ) : (
              <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontStyle: 'italic', marginTop: '8px' }}>
                Esta lección no tiene podcast de apoyo.
              </p>
            )}
          </div>

          {/* 3) Imágenes de apoyo */}
          <div style={{ background: '#0f2035', border: '1px solid rgba(197,160,89,0.25)', borderRadius: '16px', padding: '1.25rem' }}>
            <span style={S2.tag}>🖼️ Imágenes de apoyo</span>
            {images.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '8px' }}>
                {images.map((im, i) => (
                  <ProtectedImage key={im.id || i} src={im.url} alt={`Imagen de apoyo ${i + 1}`} caption={im.caption} />
                ))}
              </div>
            ) : (
              <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontStyle: 'italic', marginTop: '8px' }}>
                Esta lección no tiene imágenes de apoyo.
              </p>
            )}
          </div>

          {/* 4) Infografía */}
          {lesson.infographic_url && (
            <div style={{ background: '#0f2035', border: '1px solid rgba(197,160,89,0.25)', borderRadius: '16px', padding: '1.25rem' }}>
              <span style={S2.tag}>🖼️ Infografía (solo lectura)</span>
              <ProtectedImage src={lesson.infographic_url} alt="Infografía de la lección" caption="Infografía · solo lectura" />
            </div>
          )}

          {/* 5) Presentación de diapositivas */}
          {lesson.slides_url && (
            <div style={{ background: '#0f2035', border: '1px solid rgba(197,160,89,0.25)', borderRadius: '16px', padding: '1.25rem' }}>
              <span style={S2.tag}>📑 Presentación de diapositivas (solo lectura)</span>
              <InlinePdf url={lesson.slides_url} name="Presentación de diapositivas (solo lectura)" />
            </div>
          )}
        </div>
      );
    }

    if (active?.type === 'exam' && active.exam) {
      const questions = loadingExamAcademy
        ? []
        : (examAcademyQuestions || active.exam.questions || []);
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          <div>
            <span style={{ fontSize: '0.72rem', color: 'var(--gold-matte)', fontFamily: 'monospace', textTransform: 'uppercase', fontWeight: 'bold' }}>
              {active.tema.title} {active.lesson ? `· ${active.lesson.title}` : ''}
            </span>
            <h1 style={{ fontFamily: 'var(--font-tactical)', fontSize: '1.4rem', color: '#fff', margin: '4px 0 0' }}>
              {active.exam.title}
            </h1>
            <p style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', margin: '6px 0 0' }}>
              {questions.length} preguntas · Tiempo: {questions.length} min · Apruebas con 70% o más.
            </p>
          </div>
          <div style={{ background: '#0f2035', border: '1px solid rgba(197, 160, 89, 0.2)', borderRadius: '14px', padding: '1.25rem' }}>
            {loadingExamAcademy ? (
              <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                Cargando examen de Vortex Academy...
              </p>
            ) : questions.length > 0 ? (
              <ExamFinal
                questions={questions}
                initialResult={activeExamResult}
                onFinish={(result) => saveTemaExamResult(active.tema.id, active.exam.id, result)}
              />
            ) : (
              <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                Este examen aún no tiene preguntas configuradas.
              </p>
            )}
          </div>
        </div>
      );
    }

    if (active?.type === 'temaFinal' && active.tema) {
      const finalExam = getTemaExamenFinal(active.tema);
      const finalQuestions = finalExam?.questions || [];
      const temaDone = isTemaExamenesAprobados(active.tema, progress);
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          <div>
            <span style={{ fontSize: '0.72rem', color: 'var(--gold-matte)', fontFamily: 'monospace', textTransform: 'uppercase', fontWeight: 'bold' }}>
              {active.tema.title} · Examen Final
            </span>
            <h1 style={{ fontFamily: 'var(--font-tactical)', fontSize: '1.4rem', color: '#fff', margin: '4px 0 0' }}>
              {finalExam?.title || 'Examen Final del Tema'}
            </h1>
            <p style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', margin: '6px 0 0' }}>
              {finalQuestions.length} preguntas · Tiempo: {finalQuestions.length} min · Apruebas con 70% o más.
            </p>
          </div>

          {!temaDone ? (
            <div style={{
              background: '#0f2035', border: '1px solid rgba(197,160,89,0.2)', borderRadius: '14px',
              padding: '3rem 2rem', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem'
            }}>
              <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: 'rgba(197,160,89,0.12)', border: '1px solid rgba(197,160,89,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Lock className="w-8 h-8" style={{ color: 'var(--gold-matte)' }} />
              </div>
              <h3 style={{ fontFamily: 'var(--font-tactical)', color: '#fff', margin: 0, fontSize: '1.05rem' }}>
                Examen Final Bloqueado
              </h3>
              <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', margin: 0, maxWidth: '420px', lineHeight: 1.6 }}>
                Aprueba todos los exámenes de las lecciones de este tema (mínimo 70%) para liberar su examen final.
              </p>
            </div>
          ) : finalQuestions.length > 0 ? (
            <div style={{ background: '#0f2035', border: '1px solid rgba(197,160,89,0.2)', borderRadius: '14px', padding: '1.25rem' }}>
              <ExamFinal
                questions={finalQuestions}
                initialResult={activeExamResult}
                onFinish={(result) => saveTemaExamResult(active.tema.id, finalExam.id, result)}
              />
            </div>
          ) : (
            <div style={{
              background: '#0f2035', border: '1px solid rgba(197,160,89,0.2)', borderRadius: '14px',
              padding: '3rem 2rem', textAlign: 'center'
            }}>
              <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                El examen final de este tema aún no tiene preguntas configuradas por el administrador.
              </p>
            </div>
          )}
        </div>
      );
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
        <div>
          <span style={{ fontSize: '0.72rem', color: 'var(--gold-matte)', fontFamily: 'monospace', textTransform: 'uppercase', fontWeight: 'bold' }}>
            Evaluación Integral
          </span>
          <h1 style={{ fontFamily: 'var(--font-tactical)', fontSize: '1.4rem', color: '#fff', margin: '4px 0 0' }}>
            Examen Final del Curso
          </h1>
        </div>

        <div style={{
          background: '#0f2035', border: '1px solid rgba(197,160,89,0.2)', borderRadius: '14px',
          padding: '3rem 2rem', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem'
        }}>
          <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: 'rgba(197,160,89,0.12)', border: '1px solid rgba(197,160,89,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Award className="w-8 h-8" style={{ color: 'var(--gold-matte)' }} />
          </div>
          <h3 style={{ fontFamily: 'var(--font-tactical)', color: '#fff', margin: 0, fontSize: '1.05rem' }}>
            Cada tema tiene su examen final
          </h3>
          <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', margin: 0, maxWidth: '420px', lineHeight: 1.6 }}>
            La evaluación es por tema: aprueba los exámenes de las lecciones de cada tema (mínimo 70%) y luego su examen final.
            Cada tema aprobado desbloquea su propio certificado. Llevas <strong style={{ color: '#c5a059' }}>{completedTemas} de {totalTemas}</strong> temas completados.
          </p>
          <div style={{ width: '100%', maxWidth: '320px' }}>
            <div style={{ height: '8px', borderRadius: '99px', background: 'rgba(10,26,42,0.8)', overflow: 'hidden', border: '1px solid rgba(197,160,89,0.15)' }}>
              <div style={{ height: '100%', borderRadius: '99px', width: `${progressPct}%`, background: 'linear-gradient(90deg, #c5a059, #8f6f2f)', transition: 'width 0.4s ease' }} />
            </div>
            <p style={{ fontSize: '0.72rem', color: 'var(--text-muted)', margin: '6px 0 0', fontFamily: 'monospace' }}>
              {progressPct}% del curso completado
            </p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="vx-classroom-container">
      {/* ── Header ── */}
      <div className="vx-classroom-header">
        <button onClick={onBackToCatalog} className="btn-vx-back">
          <ArrowLeft className="w-4 h-4" />
          <span>Volver al Catálogo</span>
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', overflow: 'hidden' }}>
          <span className="vx-card-cat-badge" style={{ position: 'static' }}>
            {normCourse?.category}
          </span>
          <h2 style={{ fontFamily: 'var(--font-tactical)', fontSize: '1rem', color: '#fff', margin: 0 }} className="truncate">
            {normCourse?.title}
          </h2>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
          <button onClick={handleExitToHome} className="btn-vx-back" title="Salir y volver a la pantalla principal">
            <Home className="w-4 h-4" />
            <span>Salir</span>
          </button>
          <button
            onClick={handleLogout}
            className="btn-vx-back btn-vx-danger"
            title="Cerrar sesión"
          >
            <LogOut className="w-4 h-4" />
            <span>Cerrar Sesión</span>
          </button>
        </div>
      </div>

      {/* ── Grid: Navegación (módulos) + Contenido ── */}
      <div className={`vx-classroom-grid${navCollapsed ? ' vx-classroom-grid--collapsed' : ''}`}>
        {navCollapsed ? (
          <button
            type="button"
            onClick={() => setNavCollapsed(false)}
            className="vx-nav-expand-btn"
            title="Expandir panel de módulos de estudio"
          >
            <ChevronsRight size={20} />
            <span>Módulos</span>
          </button>
        ) : (
        <div className="vx-classroom-nav">
          <div style={{
            background: '#0f2035', border: '1px solid rgba(197, 160, 89, 0.2)',
            borderRadius: '14px', padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1rem'
          }}>
            {/* ── Módulos de Estudio (Lector de Temas) ── */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '0.68rem', color: 'var(--gold-matte)', fontFamily: 'monospace', fontWeight: 'bold', letterSpacing: '0.04em' }}>
                📖 MÓDULOS DE ESTUDIO
              </span>
              <button
                type="button"
                onClick={() => setNavCollapsed(true)}
                className="vx-nav-collapse-btn"
                title="Contraer el panel hacia el costado"
              >
                <ChevronsLeft size={14} />
                <span>Contraer</span>
              </button>
            </div>

            {jerarquia && (
              <div style={{
                fontSize: '0.66rem', color: 'var(--text-muted)', fontFamily: 'monospace',
                padding: '6px 10px', borderRadius: '8px', background: 'rgba(197,160,89,0.08)',
                border: '1px solid rgba(197,160,89,0.15)'
              }}>
                Perfil: <strong style={{ color: 'var(--gold-matte)' }}>{jerarquia}</strong>
                {cuerpo ? ` · ${cuerpo}` : ''}
                {especialidad ? ` · ${especialidad}` : ''}
              </div>
            )}

            {lectorLoading ? (
              <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>Cargando temas...</p>
            ) : !courseAreaKey ? (
              <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                Este curso no está asociado a un área de estudio del Lector.
              </p>
            ) : lectorAreas.length === 0 ? (
              <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                Configura tu perfil (jerarquía/cuerpo/especialidad) para ver tus páginas de lectura.
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxHeight: '40vh', overflowY: 'auto' }}>
                {lectorAreas.map((grp) => (
                  <div key={grp.areaKey || '__esp'} style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: '6px',
                      background: 'rgba(197,160,89,0.16)', border: '1px solid rgba(197,160,89,0.35)',
                      borderRadius: '8px', padding: '6px 10px',
                    }}>
                      <span style={{ fontSize: '0.7rem', fontWeight: 800, color: 'var(--gold-light)' }}>{grp.label}</span>
                      <span style={{ fontSize: '0.62rem', color: '#94a3b8', fontFamily: 'monospace' }}>{grp.temas.length}</span>
                    </div>
                    {grp.temas.length === 0 ? (
                      <span style={{ fontSize: '0.66rem', color: 'var(--text-muted)', fontStyle: 'italic', paddingLeft: '4px' }}>
                        Sin temas asignados a tu jerarquía.
                      </span>
                    ) : (
                      grp.temas.map((t) => {
                        const isActive = selLector?.areaKey === grp.areaKey && selLector?.tema.id === t.id;
                        const tDone = isLectorTemaCompletado(t);
                        return (
                          <div key={t.id} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <button
                              type="button"
                              onClick={() => openLectorTema(grp.areaKey, t)}
                              style={{
                                display: 'flex', alignItems: 'center', gap: '8px', width: '100%', textAlign: 'left',
                                padding: '10px 12px', borderRadius: '8px', cursor: 'pointer',
                                background: isActive ? 'rgba(197,160,89,0.18)' : 'var(--gold-matte)',
                                border: isActive ? '2px solid var(--gold-matte)' : tDone ? '1px solid rgba(52,211,153,0.6)' : '1px solid rgba(255,255,255,0.18)',
                              }}
                            >
                              <span style={{ fontSize: '0.72rem', color: tDone ? '#6ee7b7' : (isActive ? 'var(--gold-matte)' : '#ffffff'), fontWeight: 700, flex: 1, minWidth: 0, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                                {t.title}
                              </span>
                              {tDone && <CheckCircle2 className="w-3.5 h-3.5" style={{ color: '#6ee7b7', flexShrink: 0 }} />}
                              <ChevronRight size={14} style={{ color: isActive ? 'var(--gold-matte)' : '#c7c9ff', flexShrink: 0 }} />
                            </button>

                            {/* Actividades de estudio del tema del lector: flashcards + quiz evaluativo */}
                            <div style={{ padding: '0 10px 4px 16px', display: 'flex', flexDirection: 'column', gap: '3px' }}>
                              <button
                                type="button"
                                onClick={() => toggleActivities(`lector_${t.id}`)}
                                className="vx-lesson-link vx-lesson-link--block"
                                title={expandedActivities.has(`lector_${t.id}`) ? 'Ocultar actividades de estudio' : 'Mostrar actividades de estudio'}
                                style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'space-between' }}
                              >
                                <span style={{ fontSize: '0.56rem', color: 'var(--text-muted)', fontFamily: 'monospace', fontWeight: 'bold', letterSpacing: '0.05em' }}>
                                  ACTIVIDADES DE ESTUDIO
                                </span>
                                <ChevronRight size={12} style={{ color: 'var(--text-muted)', transform: expandedActivities.has(`lector_${t.id}`) ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s ease' }} />
                              </button>
                              {expandedActivities.has(`lector_${t.id}`) && (<>
                                <button
                                  type="button"
                                  onClick={() => openLectorTema(grp.areaKey, t, 'flashcards')}
                                  className="vx-lesson-link vx-lesson-link--block"
                                  title="Ver flashcards de este tema"
                                >
                                  <Layers size={12} />
                                  <span>Flashcards</span>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => openLectorTema(grp.areaKey, t, 'examen')}
                                  className="vx-lesson-link vx-lesson-link--block vx-lesson-link--exam"
                                  title="Realizar el quiz evaluativo de este tema"
                                >
                                  <FileQuestion size={12} style={{ color: 'var(--gold-matte)' }} />
                                  <span>Quiz evaluativo</span>
                                </button>
                                {lectorTemaHasFinal(t) && (
                                  <button
                                    type="button"
                                    onClick={() => openLectorTema(grp.areaKey, t, 'final')}
                                    className="vx-lesson-link vx-lesson-link--block vx-lesson-link--exam"
                                    title="Realizar el examen final de 20 preguntas del banco"
                                  >
                                    <Award size={12} style={{ color: 'var(--gold-matte)' }} />
                                    <span>Examen Final</span>
                                  </button>
                                )}
                              </>)}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* ── Progreso del curso ── */}
            <div style={{ borderTop: '1px solid rgba(197,160,89,0.2)', paddingTop: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                <span style={{ fontSize: '0.68rem', color: 'var(--text-secondary)', fontFamily: 'monospace', fontWeight: 'bold' }}>
                  PROGRESO DEL CURSO
                </span>
                <span style={{ fontSize: '0.75rem', fontWeight: 'bold', fontFamily: 'monospace', color: progressPct === 100 ? '#34d399' : 'var(--gold-matte)' }}>
                  {progressPct}%
                </span>
              </div>
              <div style={{ height: '8px', borderRadius: '99px', background: 'rgba(10, 26, 42, 0.8)', overflow: 'hidden', border: '1px solid rgba(197,160,89,0.15)' }}>
                <div style={{ height: '100%', borderRadius: '99px', width: `${progressPct}%`, background: progressPct === 100 ? 'linear-gradient(90deg, #10b981, #34d399)' : 'linear-gradient(90deg, #c5a059, #8f6f2f)', transition: 'width 0.4s ease' }} />
              </div>
              <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)', fontFamily: 'monospace', display: 'block', marginTop: '6px' }}>
                {completedTemas} de {totalTemas} temas completados
              </span>
            </div>

            {/* Badges */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
              {badges.map((b) => {
                const Icon = b.icon;
                return (
                  <div key={b.id} style={{
                    padding: '10px', borderRadius: '10px', textAlign: 'center',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px',
                    background: b.earned ? 'rgba(197, 160, 89, 0.15)' : 'rgba(10, 26, 42, 0.5)',
                    border: b.earned ? '1px solid rgba(197, 160, 89, 0.45)' : '1px solid rgba(197, 160, 89, 0.12)',
                    opacity: b.earned ? 1 : 0.45
                  }}>
                    <Icon className="w-5 h-5" style={{ color: b.earned ? 'var(--gold-matte)' : 'var(--text-muted)' }} />
                    <span style={{ fontSize: '0.62rem', color: b.earned ? '#fff' : 'var(--text-muted)', fontFamily: 'monospace', fontWeight: 'bold', lineHeight: 1.3 }}>
                      {b.label}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Navegación por temas */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '55vh', overflowY: 'auto' }}>
              {temas.map((tema, tIdx) => {
                const temaProg = temasProg[tema.id] || {};
                const temaDone = isTemaCompletado(tema, progress);
                const isActiveTema = active?.tema?.id === tema.id || active?.lesson && tema.lessons?.some((l) => l.id === active.lesson.id);
                return (
                  <div key={tema.id || tIdx} style={{
                    border: isActiveTema ? '2px solid var(--gold-matte)' : '1px solid rgba(255,255,255,0.18)',
                    background: 'var(--gold-matte)',
                    borderRadius: '10px', overflow: 'hidden'
                  }}>
                    <div style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px',
                      padding: '14px 16px', cursor: 'pointer'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', overflow: 'hidden' }}>
                        <span style={{
                          width: '32px', height: '32px', borderRadius: '50%', flexShrink: 0, fontSize: '0.82rem',
                          fontWeight: 'bold', fontFamily: 'monospace', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          background: temaDone ? 'rgba(16,185,129,0.25)' : isActiveTema ? '#162a3d' : 'rgba(255,255,255,0.18)',
                          color: temaDone ? '#6ee7b7' : isActiveTema ? '#fff' : '#ffffff'
                        }}>
                          {temaDone ? <CheckCircle2 className="w-5 h-5" /> : tIdx + 1}
                        </span>
                        <div style={{ overflow: 'hidden' }}>
                          <p style={{
                            margin: 0, fontSize: '0.9rem', fontWeight: 'bold',
                            color: 'var(--gold-matte)',
                            background: '#fff',
                            padding: '3px 10px',
                            borderRadius: '6px',
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                          }}>
                            {tema.title}
                          </p>
                          <span style={{ fontSize: '0.7rem', color: '#d6d8ff', fontFamily: 'monospace' }}>
                            {tema.lessons?.length || 0} lecciones · {tema.lessons?.reduce((a, l) => a + getLessonExamenes(l).length, 0) || 0} exámenes
                          </span>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5" style={{ color: '#d6d8ff', flexShrink: 0 }} />
                    </div>

                    {/* Lecciones del tema con sus exámenes debajo */}
                    {(tema.lessons || []).map((lesson, lIdx) => {
                      const isLessonActive = active?.type === 'lesson' && active.lesson.id === lesson.id;
                      const isLessonDone = !!temaProg.lessons?.[lesson.id];
                      const lessonExams = getLessonExamenes(lesson);
                      const lessonFlashcards = getLessonFlashcards(lesson);
                      const hasMaterial = !!(lesson.video_url || lesson.audio_url || (Array.isArray(lesson.images) && lesson.images.some((im) => im && im.url)));
                      return (
                        <div key={lesson.id || lIdx}>
                          <div
                            onClick={() => openLesson(lesson)}
                            style={{
                              padding: '8px 12px 8px 20px', cursor: 'pointer', borderTop: '1px solid rgba(255,255,255,0.14)',
                              display: 'flex', alignItems: 'center', gap: '8px',
                              background: isLessonActive ? '#162a3d' : 'transparent'
                            }}
                          >
                            <span style={{ fontSize: '0.7rem', color: isLessonActive ? 'var(--gold-matte)' : '#ffffff', fontWeight: isLessonActive ? 'bold' : 400, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                              {lesson.title}
                            </span>
                            {isLessonDone && <CheckCircle2 className="w-3.5 h-3.5" style={{ color: '#34d399', flexShrink: 0 }} />}
                          </div>

                          {/* Actividades de estudio de la lección: flashcards + quizzes + material */}
                          {(lessonFlashcards.length > 0 || lessonExams.length > 0 || hasMaterial) && (
                            <div style={{ padding: '0 12px 10px 20px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                              <button
                                type="button"
                                onClick={() => toggleActivities(`lesson_${tema.id}_${lesson.id}`)}
                                className="vx-lesson-link vx-lesson-link--block"
                                title={expandedActivities.has(`lesson_${tema.id}_${lesson.id}`) ? 'Ocultar actividades de estudio' : 'Mostrar actividades de estudio'}
                                style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'space-between' }}
                              >
                                <span style={{ fontSize: '0.58rem', color: '#d6d8ff', fontFamily: 'monospace', fontWeight: 'bold', letterSpacing: '0.05em' }}>
                                  ACTIVIDADES DE ESTUDIO
                                </span>
                                <ChevronRight size={12} style={{ color: '#d6d8ff', transform: expandedActivities.has(`lesson_${tema.id}_${lesson.id}`) ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s ease' }} />
                              </button>
                              {expandedActivities.has(`lesson_${tema.id}_${lesson.id}`) && (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                {lessonFlashcards.length > 0 && (
                                  <button
                                    type="button"
                                    onClick={() => openFlashcards(tema, lesson)}
                                    className="vx-lesson-link vx-lesson-link--block"
                                    title="Ver flashcards de esta lección"
                                  >
                                    <Layers size={12} />
                                    <span>Flashcards ({lessonFlashcards.length})</span>
                                  </button>
                                )}

                                {lessonExams.map((exam, eIdx) => {
                                  const isExamActive = active?.type === 'exam' && active.exam.id === exam.id && active.tema.id === tema.id;
                                  const stored = temaProg.examenes?.[exam.id];
                                  const examPassed = !!(stored && stored.passed);
                                  return (
                                    <button
                                      key={exam.id || eIdx}
                                      type="button"
                                      onClick={() => openExam(tema, lesson, exam)}
                                      className="vx-lesson-link vx-lesson-link--block vx-lesson-link--exam"
                                      style={{ borderColor: isExamActive ? 'var(--gold-matte)' : examPassed ? 'rgba(52,211,153,0.35)' : undefined, background: isExamActive ? 'rgba(197,160,89,0.12)' : examPassed ? 'rgba(16,185,129,0.1)' : undefined }}
                                      title={`Quiz evaluativo: ${exam.title}`}
                                    >
                                      <FileQuestion size={12} style={{ color: examPassed ? '#34d399' : 'var(--gold-matte)' }} />
                                      <span>Quiz evaluativo · {exam.title}</span>
                                      {examPassed && <CheckCircle2 className="w-3.5 h-3.5" style={{ color: '#34d399', flexShrink: 0, marginLeft: 'auto' }} />}
                                    </button>
                                  );
                                })}

                                {hasMaterial && (
                                  <button
                                    type="button"
                                    onClick={() => openMaterial(tema, lesson)}
                                    className="vx-lesson-link vx-lesson-link--block"
                                    title="Ver material de apoyo audiovisual (video, podcast, imágenes)"
                                  >
                                    <Play size={12} />
                                    <span>Material de apoyo</span>
                                  </button>
                                )}
                              </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {/* Examen final del tema */}
                    {getTemaExamenFinal(tema) && (() => {
                      const finalExam = getTemaExamenFinal(tema);
                      const examReady = isTemaExamenesAprobados(tema, progress);
                      const storedFinal = temaProg.examenes?.[finalExam.id];
                      const finalPassed = !!(storedFinal && storedFinal.passed);
                      const isFinalActive = active?.type === 'temaFinal' && active.tema.id === tema.id;
                      return (
                        <div
                          onClick={() => openTemaFinal(tema)}
                          style={{
                            padding: '8px 12px 8px 34px', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', gap: '8px',
                            background: isFinalActive ? 'rgba(197,160,89,0.12)' : 'transparent'
                          }}
                        >
                          <Award className="w-3.5 h-3.5" style={{ color: finalPassed ? '#6ee7b7' : (examReady ? 'var(--gold-matte)' : '#d6d8ff'), flexShrink: 0 }} />
                          <span style={{ fontSize: '0.68rem', color: finalPassed ? '#6ee7b7' : (examReady ? 'var(--gold-matte)' : '#d6d8ff'), fontWeight: isFinalActive ? 'bold' : 400, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                            Examen Final del Tema
                          </span>
                          {examReady && !finalPassed && <LockOpen className="w-3.5 h-3.5" style={{ color: 'var(--gold-matte)', flexShrink: 0 }} />}
                          {finalPassed && <CheckCircle2 className="w-3.5 h-3.5" style={{ color: '#34d399', flexShrink: 0 }} />}
                        </div>
                      );
                    })()}
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Certificados por tema ── */}
    {/* Certificados por tema: solo el del tema que se está cursando actualmente */}
    <div style={{
      background: '#0f2035', border: '1px solid rgba(197,160,89,0.2)', borderRadius: '14px',
      padding: '1.25rem', marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '1rem'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Award className="w-5 h-5" style={{ color: 'var(--gold-matte)' }} />
        <h3 style={{ fontFamily: 'var(--font-tactical)', fontSize: '0.85rem', color: '#fff', margin: 0 }}>
          Certificado del Tema Actual
        </h3>
      </div>
      {(() => {
        // Determina el tema activo según el tipo de actividad en curso.
        let currentKey = null;
        if (active?.type === 'lector' && selLector) {
          currentKey = `lector_${selLector.tema.id}`;
        } else if (active?.type === 'lesson' && active.lesson) {
          const tema = temas.find((t) => (t.lessons || []).some((l) => l.id === active.lesson.id));
          if (tema) currentKey = `rtdb_${tema.id}`;
        } else if (active?.tema) {
          currentKey = `rtdb_${active.tema.id}`;
        } else if (temas[0]) {
          currentKey = `rtdb_${temas[0].id}`;
        }
        const cert = currentKey ? temaCertificates.find((c) => c.key === currentKey) : null;
        if (!cert) {
          return (
            <p style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontStyle: 'italic', margin: 0 }}>
              Este tema aún no tiene certificado configurado.
            </p>
          );
        }
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <span style={{ fontSize: '0.72rem', fontWeight: 700, color: cert.enabled ? '#34d399' : '#fff', display: 'flex', alignItems: 'center', gap: '6px' }}>
              {cert.enabled ? <Award className="w-3.5 h-3.5" style={{ color: '#34d399', flexShrink: 0 }} /> : <Lock className="w-3.5 h-3.5" style={{ color: 'var(--text-muted)', flexShrink: 0 }} />}
              {cert.title}
            </span>
            <CertificateCard
              courseTitle={normCourse?.title}
              userName={userName}
              userEmail={userEmail}
              area={courseAreaKey ? (AREA_NAMES[courseAreaKey] || normCourse?.category) : normCourse?.category}
              modulo={cert.title}
              score={cert.enabled ? cert.score : undefined}
              issuedAt={cert.enabled ? cert.date : undefined}
              enabled={cert.enabled}
            />
          </div>
        );
      })()}
    </div>
        </div>
        )}

        {/* ── Columna de contenido ── */}
        <div className="vx-classroom-main">
          {renderMainContent()}
        </div>
      </div>

      {activeQuiz && (
        <ExamRunner
          quiz={activeQuiz}
          onClose={() => setActiveQuiz(null)}
          onResult={(result) => selLector && handleLectorQuizResult(selLector.tema, activeQuiz, result)}
        />
      )}
    </div>
  );
};
